import React from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory, useLocation} from 'react-router-dom'

import {Box, Grid} from '@mui/material'

import Navigation from '@progyny/pages/Consultations/Program/components/Navigation'
import {useStyles} from '@progyny/pages/Consultations/Program/components/SessionPlan'
import CancelConsultation from '@progyny/pages/Consultations/components/CancelConsultation'
import ConsultationRow from '@progyny/pages/Consultations/components/ConsultationRow'
import NoConsultationView from '@progyny/pages/Consultations/components/NoConsultationView'
import SingleSessionBooking, {
  SessionForBooking,
} from '@progyny/pages/Consultations/components/SingleSession/SingleSessionBooking'

export interface AppointmentForTabs {
  id?: string | null
  scheduledTime?: string | null
  programEnrollment?: {program?: {slug?: string | null}} | null
  calcomBookingUid?: string | null
  canReschedule?: boolean | null
  bookingLanguage?: string | null
  [key: string]: unknown
}

interface SingleSessionTabsProps {
  programSlug: string
  sessionNumber: number
  session: SessionForBooking | null | undefined
  upcomingAppointments: AppointmentForTabs[]
  pastAppointments: AppointmentForTabs[]
  colors: any
  basePath?: string
  showTabs?: boolean
  onBookingSuccessful: () => void
  onCancelBack?: () => void
  onCalendarVisibilityChange?: (visible: boolean) => void
  onTabsVisibilityChange?: (visible: boolean) => void
  isFertility?: boolean
}

const SingleSessionTabs: React.FC<SingleSessionTabsProps> = ({
  programSlug,
  sessionNumber,
  session,
  upcomingAppointments,
  pastAppointments,
  colors,
  basePath,
  showTabs = true,
  onBookingSuccessful,
  onCancelBack,
  onCalendarVisibilityChange,
  onTabsVisibilityChange,
}) => {
  const history = useHistory()
  const location = useLocation()
  const {t} = useTranslation()
  const {classes} = useStyles({colors})

  const [activeCancelUrl, setActiveCancelUrl] = React.useState<string | null>(
    null
  )
  const [showBookingCalendar, setShowBookingCalendar] = React.useState(false)
  const [isCalendarVisible, setIsCalendarVisible] = React.useState(false)
  const isTabsVisible = showTabs && !isCalendarVisible

  const handleCalendarVisibilityChange = (visible: boolean) => {
    setIsCalendarVisible(visible)
    onCalendarVisibilityChange?.(visible)
  }

  React.useEffect(() => {
    onTabsVisibilityChange?.(isTabsVisible)
  }, [isTabsVisible, onTabsVisibilityChange])

  const handleCancelBack = React.useCallback(() => {
    setActiveCancelUrl(null)
    if (onCancelBack) {
      onCancelBack()
    }
  }, [onCancelBack])

  const handleBookingBack = React.useCallback(() => {
    setShowBookingCalendar(false)
    onCancelBack?.()
  }, [onCancelBack])

  const activeNavItem = React.useMemo(() => {
    if (location.pathname.endsWith('/past')) return 'past'
    return 'upcoming'
  }, [location.pathname])

  React.useEffect(() => {
    if (activeNavItem !== 'upcoming' && activeCancelUrl) {
      setActiveCancelUrl(null)
    }
  }, [activeNavItem, activeCancelUrl])

  const navigationItems = [
    {
      itemID: 'upcoming',
      label: t('consultations_program_session_plan_upcoming_consultations'),
    },
    {
      itemID: 'past',
      label: t('consultations_program_session_plan_past_consultations'),
    },
  ]

  const handleNavItemClick = (itemID: string) => {
    const currentBasePath = basePath || location.pathname.replace('/past', '')
    if (itemID === 'past') {
      history.push(`${currentBasePath}/past`)
    } else {
      history.push(currentBasePath)
    }
  }

  const renderTabContent = () => {
    if (activeCancelUrl) {
      return (
        <CancelConsultation
          cancelUrl={activeCancelUrl}
          onBack={handleCancelBack}
        />
      )
    }
    switch (activeNavItem) {
      case 'upcoming':
        return (
          <Grid container rowGap={2}>
            {upcomingAppointments.length > 0 ? (
              upcomingAppointments.map((appointment, index) => (
                <Grid item xs={12} key={appointment?.id || index}>
                  <ConsultationRow
                    status='upcoming'
                    sessionLabel={`${t('common_session')} ${sessionNumber}`}
                    title={session?.title || ''}
                    scheduledTime={appointment?.scheduledTime}
                    colors={colors}
                    language={appointment?.bookingLanguage}
                    programSlug={programSlug}
                    sessionNumber={sessionNumber}
                    appointment={
                      appointment
                        ? {
                            id: appointment.id ?? undefined,
                            calcomBookingUid: appointment.calcomBookingUid,
                            canReschedule: appointment.canReschedule,
                          }
                        : null
                    }
                    onCancelRequested={(url) => setActiveCancelUrl(url)}
                  />
                </Grid>
              ))
            ) : (
              <Grid item xs={12} mb={10}>
                {showBookingCalendar && session ? (
                  <SingleSessionBooking
                    session={session}
                    programSlug={programSlug}
                    onBookingSuccessful={onBookingSuccessful}
                    onCalendarVisibilityChange={handleCalendarVisibilityChange}
                    onBackClick={handleBookingBack}
                  />
                ) : (
                  <NoConsultationView
                    onBookClick={() => setShowBookingCalendar(true)}
                    isOnboarding={pastAppointments.length === 0}
                  />
                )}
              </Grid>
            )}
          </Grid>
        )
      case 'past':
        return (
          <Grid container rowGap={2}>
            {pastAppointments.map((appointment, index) => (
              <Grid item xs={12} key={appointment?.id || index}>
                <ConsultationRow
                  status='completed'
                  sessionLabel={`${t('common_session')} ${sessionNumber}`}
                  title={session?.title || ''}
                  scheduledTime={appointment?.scheduledTime}
                  colors={colors}
                  language={appointment?.bookingLanguage}
                  programSlug={programSlug}
                  sessionNumber={sessionNumber}
                />
              </Grid>
            ))}
          </Grid>
        )
    }
  }

  return (
    <Box className={classes.container}>
      {isTabsVisible && (
        <Navigation
          items={navigationItems}
          activeItemID={activeNavItem}
          onItemClick={handleNavItemClick}
          colors={colors}
        />
      )}
      {renderTabContent()}
    </Box>
  )
}

export default SingleSessionTabs
